import SmsIcon from "src/assets/icons/sms"
import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import useSupport from "src/hooks/useSupport"

const ReportConcernForm = () => {
  // Define issue types with keys and labels
  const issueTypes = [
    { label: "TECHNICAL", key: "technical" },
    { label: "USABILITY", key: "usability" },
    { label: "VISUAL", key: "visual" },
    { label: "OTHERS", key: "other" },
  ]

  // State management - use the key instead of the label
  const [issueType, setIssueType] = useState(issueTypes[0].key)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  })

  // Redux state
  const { ownDetails } = useSelector((state) => state.profile)
  const { isReportConcernLoading } = useSelector((state) => state.support)
  const { handleReportConcern } = useSupport()

  // Form handling
  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: formData,
  })

  // Prefill form with user details
  useEffect(() => {
    if (ownDetails?.details) {
      const userDetails = ownDetails.details
      setFormData((prev) => ({
        ...prev,
        firstName: userDetails.firstName || "",
        lastName: userDetails.lastName || "",
        email: userDetails.email || "",
      }))

      // Update form control values
      setValue("firstName", userDetails.firstName || "")
      setValue("lastName", userDetails.lastName || "")
      setValue("email", userDetails.email || "")
    }
  }, [ownDetails, setValue])

  // Handle input changes - now only for the message field
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    setValue(field, value)
  }

  // Form submission
  const onSubmit = async (data) => {
    try {
      const messageToSend = formData.message

      // console.log("Sending report with message:", messageToSend)
      // console.log("Issue type (key):", issueType)

      if (messageToSend && messageToSend.trim() !== "") {
        await handleReportConcern({
          message: messageToSend,
          issueType: issueType, // Send the key directly
        })

        // Clear just the message
        handleInputChange("message", "")
      } else {
        alert("Please enter a message before submitting")
      }
    } catch (error) {
      console.error("Failed to send report:", error)
    }
  }

  return (
    <form
      className="bg-white shadow-md rounded-2xl p-6 md:max-w-xl max-w-md"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h2 className="text-3xl font-normal text-[#005BB2] mb-4">
        Report Concern
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Our friendly team would love to hear from you.
      </p>

      <div className="flex gap-4 mb-4">
        <div className="flex-1">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="firstName"
          >
            First Name*
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            readOnly
            className="mt-1 p-3 w-full border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
          {errors.firstName && (
            <p className="text-red-500 text-xs mt-1">
              {errors.firstName.message}
            </p>
          )}
        </div>
        <div className="flex-1">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="lastName"
          >
            Last Name*
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            readOnly
            className="mt-1 p-3 w-full border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
          {errors.lastName && (
            <p className="text-red-500 text-xs mt-1">
              {errors.lastName.message}
            </p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="email"
        >
          Email*
        </label>
        <div className="relative">
          <span className="absolute inset-y-0 left-3 flex items-center">
            <SmsIcon />
          </span>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            readOnly
            className="p-3 pl-10 w-full border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
          />
        </div>
        {errors.email && (
          <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Issue Type - updated to use the issueTypes array */}
      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-2">
          Issue Type
        </span>
        <div className="flex flex-wrap gap-2">
          {issueTypes.map((type) => (
            <button
              key={type.key}
              type="button"
              onClick={() => setIssueType(type.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                issueType === type.key
                  ? "bg-[#005BB2] text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {type.label.charAt(0) + type.label.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Message - remains editable */}
      <div className="mb-6">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="message"
        >
          Message*
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={(e) => handleInputChange("message", e.target.value)}
          placeholder="Enter your concern details..."
          className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          rows={4}
        />
        {errors.message && (
          <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>
        )}
      </div>
      <button
        type="submit"
        disabled={isReportConcernLoading}
        className={`w-full bg-[#005BB2] text-white py-3 rounded-md text-sm font-medium hover:bg-[#004499] focus:outline-none focus:ring-2 focus:ring-blue-400 ${
          isReportConcernLoading ? "opacity-70 cursor-not-allowed" : ""
        }`}
      >
        {isReportConcernLoading ? "SENDING..." : "SEND REPORT"}
      </button>
    </form>
  )
}

export default ReportConcernForm
